import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function SignupVerify() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState("loading"); // loading | ok | fail
  const [message, setMessage] = useState("");
  const startedRef = useRef(false);

  useEffect(() => {
    if (!token) { setState("fail"); setMessage("Missing verification token."); return; }
    if (startedRef.current) return; // guard against React StrictMode double-invocation
    startedRef.current = true;
    (async () => {
      try {
        const { data } = await api.post("/signup/verify", { token });
        setMessage(`Account created for ${data.manager_email}. Redirecting to sign in…`);
        setState("ok");
        toast.success("Account verified — please sign in");
        setTimeout(() => navigate("/login", { replace: true }), 1200);
      } catch (err) {
        setState("fail");
        setMessage(formatApiError(err?.response?.data?.detail) || "Verification failed.");
      }
    })();
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-primary text-primary-foreground grid place-items-center p-6" data-testid="signup-verify-page">
      <div className="max-w-md w-full bg-card text-card-foreground rounded-[10px] p-8 border border-border">
        {state === "loading" && (
          <div className="flex flex-col items-center text-center gap-3" data-testid="signup-verify-loading">
            <Loader2 className="h-10 w-10 text-accent animate-spin" />
            <div className="font-extrabold text-lg">Verifying your email…</div>
            <div className="text-sm text-muted-foreground">One moment while we set up your tenant.</div>
          </div>
        )}
        {state === "ok" && (
          <div className="flex flex-col items-center text-center gap-3" data-testid="signup-verify-success">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
            <div className="font-extrabold text-lg">You're all set</div>
            <div className="text-sm text-muted-foreground">{message}</div>
            <Link to="/login" className="text-accent font-semibold underline" data-testid="signup-verify-login-link">Go to sign in</Link>
          </div>
        )}
        {state === "fail" && (
          <div className="flex flex-col items-center text-center gap-3" data-testid="signup-verify-fail">
            <XCircle className="h-10 w-10 text-destructive" />
            <div className="font-extrabold text-lg">Couldn't verify this link</div>
            <div className="text-sm text-muted-foreground">{message}</div>
            <Button asChild variant="outline" data-testid="signup-verify-back"><Link to="/signup">Start over</Link></Button>
          </div>
        )}
      </div>
    </div>
  );
}
